
import { reactive } from 'vue';
import axios from 'axios';

export const authState = reactive({
  isAuthenticated: false,
  emailVerified: false,
  isAdmin: false,
  initialized: false,
  // Stable authenticated member id from GET /auth/me. Consumed by per-member
  // client state (e.g. the workspace subsystem's storage namespace). Null when
  // signed out or when /auth/me has not resolved.
  userId: null,
});

let resolveAuthReady;
export const authReady = new Promise((resolve) => {
  resolveAuthReady = resolve;
});

export function setEmailVerified(value) {
  authState.emailVerified = value === true;
}

export function setIsAdmin(value) {
  authState.isAdmin = value === true;
}

export function setUserId(value) {
  authState.userId = Number.isInteger(value) ? value : null;
}

export function clearAuthProfileFlags() {
  setEmailVerified(false);
  setIsAdmin(false);
  setUserId(null);
}

/**
 * Refresh email_verified and is_admin from /auth/me when authenticated.
 * @returns {Promise<object|null>} auth/me payload, or null
 */
export async function refreshAuthProfile() {
  if (!authState.isAuthenticated) {
    clearAuthProfileFlags();
    return null;
  }
  try {
    const response = await axios.get('/api/v1/auth/me');
    setEmailVerified(response.data?.email_verified === true);
    setIsAdmin(response.data?.is_admin === true);
    setUserId(response.data?.id);
    return response.data;
  } catch {
    clearAuthProfileFlags();
    return null;
  }
}

/**
 * Refresh email_verified from /auth/me when authenticated.
 * @returns {Promise<boolean>}
 */
export async function refreshEmailVerified() {
  await refreshAuthProfile();
  return authState.emailVerified;
}

export async function checkAuth() {
  try {
    const response = await axios.get('/api/v1/auth/check_login_status');
    authState.isAuthenticated = response.data.isAuthenticated === true;
    if (authState.isAuthenticated) {
      await refreshAuthProfile();
    } else {
      clearAuthProfileFlags();
    }
  } catch {
    authState.isAuthenticated = false;
    clearAuthProfileFlags();
  } finally {
    authState.initialized = true;
    resolveAuthReady();
  }
}
